import { useEffect, useState } from "react";
import {
  Lightbulb, ChatCircleDots, FilmSlate, CreditCard, ShieldCheck, Flag, PaperPlaneTilt, Camera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import client, { captureScreenshot } from "@/lib/api";

// The single adaptive form behind every "Send feedback" / "Report" / "Wrong
// info?" entry point in the app — one pipeline, six categories. See
// /app/memory/PRD.md "Feedback & Support System" for the full spec.
export const CATEGORIES = [
  { key: "feature", label: "Suggest a feature", icon: Lightbulb,
    placeholder: "What would you like Screening Circle to do?" },
  { key: "general", label: "General feedback", icon: ChatCircleDots,
    placeholder: "What's on your mind? We read every note." },
  { key: "title_info", label: "Wrong title info", icon: FilmSlate,
    placeholder: "What's wrong with this title's info?" },
  { key: "account_billing", label: "Account & billing help", icon: CreditCard,
    placeholder: "What can we help with — your account or billing?" },
  { key: "privacy", label: "Privacy request", icon: ShieldCheck,
    placeholder: "Tell us what you'd like — access, correct, or delete your data" },
  { key: "report_content", label: "Report a user or content", icon: Flag,
    placeholder: "What happened? Tell us what you saw." },
];
const REPLY_PROMISED = new Set(["account_billing", "privacy", "report_content"]);
const SLA_TEXT = { account_billing: "within 2 business days", privacy: "within 30 days", report_content: "within 24 hours" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * target: { type, id, label, tmdb_id, media_type } — pre-attaches context
 * (e.g. a title) so the member doesn't have to describe what they mean.
 */
export function FeedbackModal({ open, onClose, defaultCategory = "feature", target = null, lockCategory = false }) {
  const { user } = useAuth();
  const [category, setCategory] = useState(defaultCategory);
  const [message, setMessage] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [contactOk, setContactOk] = useState(true);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setCategory(defaultCategory); setMessage(""); setResult(null); setIncludeScreenshot(false); }
  }, [open, defaultCategory]);

  const reset = () => {
    setCategory(defaultCategory); setMessage(""); setResult(null);
    setIncludeScreenshot(false); setContactOk(true); setContactName(""); setContactEmail("");
  };
  const close = () => { onClose?.(); reset(); };
  const active = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];
  const replyPromised = REPLY_PROMISED.has(category);

  const submit = async () => {
    const msg = message.trim();
    if (msg.length < 3) { toast.error("Add a little more detail first"); return; }
    if (!user && category !== "account_billing") { toast.error("Please sign in to send this kind of feedback."); return; }
    if (!user) {
      if (!contactName.trim() || !EMAIL_RE.test(contactEmail.trim())) {
        toast.error("Please enter your name and a valid email."); return;
      }
    }
    setSending(true);
    try {
      let screenshot;
      if (includeScreenshot && category === "title_info") screenshot = await captureScreenshot();
      const { data } = await client.post("/support/submit", {
        category, message: msg, screenshot: screenshot || undefined,
        contact_ok: replyPromised ? true : contactOk,
        target: target || undefined,
        contact_name: user ? undefined : contactName.trim(),
        contact_email: user ? undefined : contactEmail.trim(),
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="feedback-modal" className="max-w-md border border-gold/25 bg-[#0B0F15] text-[#F6F5F1]">
        <DialogTitle className="font-display text-xl font-semibold text-[#F6F5F1]">
          {target?.label ? `About "${target.label}"` : "Send feedback"}
        </DialogTitle>

        {result ? (
          <div data-testid="feedback-modal-success" className="py-6 text-center">
            <p className="text-[15px] font-bold text-emerald-300">Thanks — got it!</p>
            <p className="mt-2 text-[13px] text-dim">Reference</p>
            <p data-testid="feedback-modal-ref" className="font-display text-2xl font-bold text-gold">{result.ref}</p>
            {result.needs_reply ? (
              <p className="mt-2 text-[13px] text-dim">We'll reply {result.sla_text || "soon"}.</p>
            ) : (
              <p className="mt-2 text-[13px] text-dim">No reply needed — this goes straight to our backlog.</p>
            )}
            <button
              data-testid="feedback-modal-done"
              onClick={close}
              className="mt-5 rounded-full border border-white/15 px-5 py-2 text-[13px] font-bold text-dim transition-colors hover:text-[#F6F5F1]"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {!lockCategory && (
              <div className="flex flex-wrap gap-2" data-testid="feedback-modal-category-group">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const isActive = category === c.key;
                  return (
                    <button
                      key={c.key}
                      data-testid={`feedback-modal-category-${c.key}`}
                      onClick={() => setCategory(c.key)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-bold transition-colors ${isActive ? "border-gold bg-gold/15 text-gold" : "border-white/[0.12] text-dim hover:text-[#F6F5F1]"}`}
                    >
                      <Icon size={14} weight={isActive ? "fill" : "regular"} /> {c.label}
                    </button>
                  );
                })}
              </div>
            )}

            {target?.label && (
              <div data-testid="feedback-modal-target-chip" className="rounded-[10px] border border-white/[0.1] bg-white/[0.03] px-3 py-2 text-[12px] text-dim">
                Attached: <span className="font-bold text-[#F6F5F1]">{target.label}</span>
              </div>
            )}

            <Textarea
              data-testid="feedback-modal-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={active.placeholder}
              className="min-h-[100px] border-white/[0.12] bg-white/[0.03] text-[#F6F5F1] placeholder:text-faint"
              maxLength={3000}
            />

            {category === "title_info" && (
              <label className="flex items-center gap-2 text-[12px] text-dim" data-testid="feedback-modal-screenshot-toggle">
                <Checkbox checked={includeScreenshot} onCheckedChange={(v) => setIncludeScreenshot(!!v)} />
                <Camera size={14} /> Attach a screenshot
              </label>
            )}

            {!replyPromised && (
              <label className="flex items-center gap-2 text-[12px] text-dim" data-testid="feedback-modal-contact-toggle">
                <Checkbox checked={contactOk} onCheckedChange={(v) => setContactOk(!!v)} />
                It's okay to contact me about this
              </label>
            )}

            {!user && category === "account_billing" && (
              <div className="space-y-2" data-testid="feedback-modal-guest-fields">
                <Input
                  data-testid="feedback-modal-guest-name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Your name"
                  className="border-white/[0.12] bg-white/[0.03] text-[#F6F5F1] placeholder:text-faint"
                />
                <Input
                  data-testid="feedback-modal-guest-email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="Your email"
                  className="border-white/[0.12] bg-white/[0.03] text-[#F6F5F1] placeholder:text-faint"
                />
              </div>
            )}

            {replyPromised && (
              <p className="text-[11px] text-faint">We'll reply {SLA_TEXT[category]}.</p>
            )}

            <button
              data-testid="feedback-modal-submit"
              onClick={submit}
              disabled={sending || message.trim().length < 3}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <PaperPlaneTilt size={15} weight="bold" /> {sending ? "Sending…" : "Send"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackModal;
